import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { Button } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { PageLayout } from '../../PageLayout';
import { DetailViewCard } from '../../Compounds/CardWithTitle/DetailViewCard';
import { fetchRpmAccount, getRpmAccountSubject, RpmAccount, updateRpmAccount } from '../../../store/RpmAccount';
import { AssetMiniTable } from '../../Compounds/Asset/AssetMiniTable';
import { ProvisionalAssetMiniTable } from '../../Compounds/ProvisionalAsset/ProvisionalAssetMiniTable';
import { DetailsLinkCTA } from '../../Compounds/DetailsLinkCTA';
import { NewLinkDialog } from '../../Compounds/Linking/NewLinkDialog/NewLinkDialog';
import { LabelValueField } from '../../Compounds/DetailPanel/LabelValueField';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { addDrawer, getFromDrawerSubject } from '../../../store/Drawer';
import { isAnalyst, isData, isObserver } from '../../../store/User';
import { rpmSections } from './RPMAccountDetail';
import { DetailPanelContainer } from '../../Compounds/DetailPanel/DetailPanelContainer';
import { getFormattedDate } from '../../../utils/helpers';
import { RPMAcctRunTotals } from '../../Compounds/RunTotal/RPMAcctRunTotals';
import { TicketMiniTable } from '../../Compounds/Ticket/TicketMiniTable';
import { fetchTicketsByRpmAcctId } from '../../../store/Ticket';
import { fetchAssetsByRpmAcctId, getAssetsByRpmAcctLoadingSubject, getAssetsByRpmAcctSub } from '../../../store/Asset';
import { deleteLink } from '../../../store/Link';
import {
  fetchProvisionalAssetsByRpmAcctId,
  getProvisionalAssetsByRpmAcctLoadingSubject,
  getProvisionalAssetsByRpmAcctSub
} from '../../../store/ProvisionalAsset';
import { DetailsComments } from '../../Compounds/DetailsComments';
import { EntityType, TypeAsset } from '../../../store/Search';
import { DeleteRpmAccountBtn } from './DeleteRpmAccountBtn';
import { getSelectedTab, updateState } from '../../../store/GeneralStore';
import { TabPanel } from '../../Atoms/TabPanel';
import { AllocationMiniTable } from '../../Compounds/Allocation/AllocationMiniTable';

const tabs = ['Info', 'Allocation'];

export const RPMAccountDetailPage = () => {
  const { id } = useParams();
  const { link } = getMetaData({ name: ComponentName.RPM_ACCOUNT, id: id || '' });
  const [data, setData] = useState<RpmAccount>({} as RpmAccount);
  const [isLinkFormOpen, setIsLinkFormOpen] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState<number>(
    getSelectedTab().rpmAccountDetail ? Number(getSelectedTab().rpmAccountDetail) : 0
  );
  const [typeAsset, setTypeAsset] = useState<TypeAsset>(EntityType.ASSET);
  const navigate = useNavigate();

  const handleTabChange = (tabIndex: number) => {
    setTabIndex(tabIndex);
    updateState({
      selectedTab: {
        ...getSelectedTab(),
        rpmAccountDetail: tabIndex
      }
    });
  };

  const cardActions = (typeAsset: TypeAsset) => {
    return isObserver() || isData() ? (
      <></>
    ) : (
      <DetailsLinkCTA
        setIsFormOpen={() => {
          setIsLinkFormOpen(true);
          setTypeAsset(typeAsset);
        }}
      />
    );
  };

  const handleBack = () => {
    if (getFromDrawerSubject().getValue()) {
      addDrawer({
        id: v4(),
        data,
        ...getMetaData({ name: ComponentName.RPM_ACCOUNT, id: data.accountId })
      });
    }
    navigate(-1);
  };

  useEffect(() => {
    const rpmSub = getRpmAccountSubject().subscribe((d) => setData(d));
    return () => {
      if (rpmSub) rpmSub.unsubscribe();
    };
  }, []);

  const getData = async () => {
    if (!id) return;

    const errorStatusCode = await fetchRpmAccount(id);
    if (errorStatusCode) navigate(`/error/${errorStatusCode}/RPM-account/${id}/${new Date().getTime()}`);
  };

  useEffect(() => {
    getData();
  }, [id]);

  const pageActions = () => {
    return (
      <Button
        color="secondary"
        variant="outlined"
        startIcon={<ArrowBackIosIcon />}
        onClick={handleBack}>
        Back
      </Button>
    );
  };

  const tabContent = () => {
    switch (tabIndex) {
      case 0:
        return (
          <div className="space-y-6">
            <DetailViewCard
              enableCollapse
              key="commissions_detail"
              title="Commissions">
              <RPMAcctRunTotals rpmAcctId={data.accountId} />
            </DetailViewCard>
            <DetailViewCard
              cardActions={cardActions(EntityType.ASSET)}
              enableCollapse
              key="asset_table"
              title="Assets">
              <AssetMiniTable
                id={id as string}
                fetchFunction={fetchAssetsByRpmAcctId}
                removeFunction={deleteLink}
                dataSubject={getAssetsByRpmAcctSub}
                loadingSubject={getAssetsByRpmAcctLoadingSubject}
                showRemove
              />
            </DetailViewCard>
            <DetailViewCard
              cardActions={cardActions(EntityType.PROVISIONAL_ASSET)}
              enableCollapse
              key="provisional_asset_table"
              title="Provisional Assets">
              <ProvisionalAssetMiniTable
                id={id as string}
                fetchFunction={fetchProvisionalAssetsByRpmAcctId}
                removeFunction={deleteLink}
                dataSubject={getProvisionalAssetsByRpmAcctSub}
                loadingSubject={getProvisionalAssetsByRpmAcctLoadingSubject}
                showRemove
              />
            </DetailViewCard>
          </div>
        );
      case 1:
        return (
          <AllocationMiniTable
            id={id as string}
            type="rpm-accounts"
          />
        );
      default:
        return null;
    }
  };

  const saveAction = (update: RpmAccount) => updateRpmAccount(update);

  const deleteButton = () => {
    if (
      isObserver() ||
      isAnalyst() ||
      isData() ||
      (data.linkCount !== null && Number(data.linkCount) > 0) ||
      (data.ticketCount !== null && Number(data.ticketCount) > 0)
    )
      return <></>;

    return <DeleteRpmAccountBtn accountId={data.accountId} />;
  };

  return (
    <PageLayout
      className="mb-10"
      dataTestId="rpm-account-detail-page"
      subTitle="RPM Account"
      pageTitle={data.accountNumber}
      showLink={true}
      link={link}
      showIcon={true}
      icon="rpmAccountMed"
      key={data.accountId}
      showCopy={true}
      copyValue={data.accountNumber}
      pageActions={pageActions()}
      tooltipLinkText="Open in RPM"
      tabs={tabs}
      handleTabChange={handleTabChange}
      activeTab={tabIndex}>
      <div className="grid grid-cols-3 mt-2 space-x-6">
        {tabs.map((tab, index) => (
          <TabPanel
            key={tab}
            value={tabIndex}
            index={index}
            boxPaddingTop={0}
            className="col-span-2">
            {tabContent()}
          </TabPanel>
        ))}
        <div className="space-y-6">
          <DetailViewCard
            enableCollapse
            key="rpm-account-detail-card"
            title="Info">
            <div className="min-h-[15rem]">
              <DetailPanelContainer
                data={rpmSections(data)}
                saveAction={(d) => saveAction(d as RpmAccount)}
              />
            </div>
          </DetailViewCard>
          <TicketMiniTable
            id={data.accountId}
            fetchFunction={fetchTicketsByRpmAcctId}
            association={{
              type: 'rmpAccount',
              body: {
                entityId: String(data.accountId)
              }
            }}
            showCreate={true}
            objectType="rpmAccount"
            objectData={data}
            objectLinkId={data.accountId}
            objectLinkDisplay={`RPM Account ${data.accountNumber}`}
          />
          <DetailsComments
            id={data.accountId}
            entityType="rpm_account"
          />
          <DetailViewCard
            enableCollapse
            key="meta_detail"
            title="Meta">
            <div className="flex px-4 pt-4 w-full items-center">
              <LabelValueField
                label="RPM Account ID"
                value={data.accountId}
                field="accountId"
              />
              <LabelValueField
                label="SFDC SR #"
                value={data.serialNumber || ''}
                field="serialNumber"
              />
            </div>
            <div className="px-4">
              <LabelValueField
                label="Added"
                value={getFormattedDate(data.addedDate)}
                field="addedDate"
              />
            </div>
            {deleteButton()}
          </DetailViewCard>
        </div>
        <NewLinkDialog
          open={isLinkFormOpen}
          setOpen={setIsLinkFormOpen}
          rpmAccount={data}
          typeAsset={typeAsset}
        />
      </div>
    </PageLayout>
  );
};
